import { Alert, Form, Input, Modal, Radio, Typography } from 'antd'

import type { GpuSchemas } from '@/api/gpu/client'

type Reason = GpuSchemas['Reason']

const MAX_NOTE = 2048

const REASONS: { value: Reason; label: string }[] = [
  { value: 'abuse', label: 'Abuse of the usage policy' },
  { value: 'provider_complaint', label: 'Complaint from the provider' },
  { value: 'operator', label: 'Operator decision' },
]

interface Values {
  reason: Reason
  note: string
  name: string
}

interface Props {
  open: boolean
  name: string
  loading: boolean
  onSubmit: (reason: Reason, note: string) => Promise<unknown>
  onClose: () => void
}

export function TerminateInstanceModal({
  open,
  name,
  loading,
  onSubmit,
  onClose,
}: Props) {
  const [form] = Form.useForm<Values>()
  const typed = Form.useWatch('name', form)

  const submit = async () => {
    const values = await form.validateFields()
    await onSubmit(values.reason, values.note.trim())
    onClose()
  }

  return (
    <Modal
      title={`Terminate GPU instance ${name}`}
      open={open}
      okText={`Terminate ${name}`}
      okType="danger"
      okButtonProps={{ disabled: typed !== name }}
      onOk={() => void submit().catch(() => undefined)}
      onCancel={onClose}
      confirmLoading={loading}
      destroyOnHidden
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Ends the instance now and closes its lease and billing. This cannot be undone."
        description="The user sees that an operator ended it; the reason and note stay internal."
      />
      <Form<Values> form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="reason"
          label="Reason"
          rules={[{ required: true, message: 'Choose a reason' }]}
        >
          <Radio.Group>
            {REASONS.map((r) => (
              <Radio key={r.value} value={r.value} style={{ display: 'flex' }}>
                {r.label}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="note"
          label="Note"
          rules={[
            { required: true, whitespace: true, message: 'Add a note' },
            { max: MAX_NOTE },
          ]}
        >
          <Input.TextArea
            rows={3}
            maxLength={MAX_NOTE}
            showCount
            placeholder="What happened and where the evidence is: complaint, ticket, logs"
          />
        </Form.Item>
        <Form.Item
          name="name"
          label={
            <span>
              Type <Typography.Text strong>{name}</Typography.Text> to confirm
            </span>
          }
        >
          <Input
            placeholder={name}
            autoComplete="off"
            status={typed && typed !== name ? 'error' : undefined}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
